import { startOperation } from '../telemetry/client';
import { createPortal } from 'react-dom';
import { searchableText } from '../../i18n/core';
import { useLocale, useTranslation } from '../../i18n/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { appHref, hrefFor, navigateTo } from '../../routing/browser';
import { getPromptTemplate, promptLibraryTemplates as templates } from './templateCatalog';
import { createPromptValues, getMissingPromptFields, renderPrompt } from './engine';
import { promptDraftRepository } from './repository';
import type { PromptTemplate, PromptValues } from './types';
import './prompts.css';

export { getPromptTemplate } from './templateCatalog';

export function promptTemplateHref(templateId: string) {
  return `${hrefFor('prompts')}?template=${encodeURIComponent(templateId)}`;
}

type PromptCategoryId = 'beverage' | 'food' | 'beauty';
const promptsPerPage = 9;

type PromptCategory = {
  id: PromptCategoryId;
  label: string;
  description: string;
  subcategories: Array<{ id: string; label: string }>;
};

const promptCategories: PromptCategory[] = [
  {
    id: 'beverage',
    label: 'Poster nước giải khát',
    description: 'Nước trái cây, sữa, cà phê, tăng lực và thức uống thiên nhiên.',
    subcategories: [
      { id: 'all', label: 'Tất cả' },
      { id: 'juice', label: 'Juice' },
      { id: 'milk-drink', label: 'Milk Drink' },
      { id: 'coffee', label: 'Coffee' },
      { id: 'energy', label: 'Tăng lực' },
      { id: 'aloe-vera', label: 'Aloe vera' },
    ],
  },
  {
    id: 'food',
    label: 'Poster thực phẩm',
    description: 'Món ăn, bánh ngọt và sản phẩm thực phẩm đóng gói.',
    subcategories: [
      { id: 'all', label: 'Tất cả' },
      { id: 'fast-food', label: 'Đồ ăn nhanh' },
      { id: 'dessert', label: 'Bánh & tráng miệng' },
      { id: 'packaged-food', label: 'Thực phẩm đóng gói' },
    ],
  },
  {
    id: 'beauty',
    label: 'Poster sản phẩm làm đẹp',
    description: 'Skincare, mỹ phẩm và sản phẩm chăm sóc cá nhân.',
    subcategories: [
      { id: 'all', label: 'Tất cả' },
      { id: 'skincare', label: 'Skincare' },
      { id: 'makeup', label: 'Mỹ phẩm' },
      { id: 'personal-care', label: 'Chăm sóc cá nhân' },
    ],
  },
];

const promptTemplateTaxonomy: Record<string, { category: PromptCategoryId; subcategory: string }> = {
  'premium-dark-splash': { category: 'beverage', subcategory: 'juice' },
  'frozen-fruit-macro': { category: 'beverage', subcategory: 'juice' },
  'dynamic-ingredient-splash': { category: 'beverage', subcategory: 'juice' },
  'natural-basket-lifestyle': { category: 'beverage', subcategory: 'aloe-vera' },
  'bright-orange-platform': { category: 'beverage', subcategory: 'juice' },
  'premium-fruit-beverage-hero': { category: 'beverage', subcategory: 'energy' },
  'frozen-coconut-strawberry': { category: 'beverage', subcategory: 'juice' },
  'summer-beach-juice': { category: 'beverage', subcategory: 'juice' },
  'pastel-tropical-juice': { category: 'beverage', subcategory: 'juice' },
  'tropical-coconut-juice': { category: 'beverage', subcategory: 'juice' },
  'milk-drink-spiral': { category: 'beverage', subcategory: 'milk-drink' },
};

function imageUrl(template: PromptTemplate) {
  return appHref(template.thumbnail.replace(/^\/?assets\//, 'assets/'));
}

export function PromptLibraryPage({ query, setQuery, savedIds, toggleSaved, requestedTemplate }: { query: string; setQuery: (value: string) => void; savedIds: string[]; toggleSaved: (id: string) => void; requestedTemplate?: string }) {
  const tr = useTranslation();
  const locale = useLocale();
  const active = requestedTemplate ? getPromptTemplate(requestedTemplate) : undefined;
  const closeEditor = useCallback(() => navigateTo('prompts', undefined, undefined, { replace: true }), []);
  const [categoryId, setCategoryId] = useState<PromptCategoryId>('beverage');
  const [subcategoryId, setSubcategoryId] = useState('all');
  const [pagination, setPagination] = useState({ filterKey: '', page: 1 });
  const resultsRef = useRef<HTMLDivElement>(null);
  const activeCategory = promptCategories.find(category => category.id === categoryId) ?? promptCategories[0];
  const needle = query.trim().toLocaleLowerCase('vi-VN');
  const categoryTemplates = useMemo(() => templates.filter(template => promptTemplateTaxonomy[template.id]?.category === categoryId), [categoryId]);
  const filtered = useMemo(() => categoryTemplates.filter(template => {
    const taxonomy = promptTemplateTaxonomy[template.id];
    if (subcategoryId !== 'all' && taxonomy?.subcategory !== subcategoryId) return false;
    return !needle || searchableText(locale, [template.cardTitle, template.title, template.caption, ...template.fields.flatMap(field => [field.label, field.value])]).includes(needle);
  }), [categoryTemplates, needle, subcategoryId, locale]);
  const filterKey = JSON.stringify([categoryId, subcategoryId, needle]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / promptsPerPage));
  const currentPage = pagination.filterKey === filterKey ? Math.min(pagination.page, pageCount) : 1;
  const pageStart = (currentPage - 1) * promptsPerPage;
  const visibleTemplates = filtered.slice(pageStart, pageStart + promptsPerPage);
  useEffect(() => { setPagination({ filterKey, page: 1 }); }, [filterKey]);

  const choosePage = (page: number) => {
    if (page === currentPage) return;
    setPagination({ filterKey, page });
    resultsRef.current?.focus({ preventScroll: true });
    resultsRef.current?.scrollIntoView({ block: 'start' });
  };

  const chooseCategory = (nextCategory: PromptCategoryId) => {
    setCategoryId(nextCategory);
    setSubcategoryId('all');
    setQuery('');
  };

  return <div className="page-wrap prompt-library-page">
    <header className="prompt-library-heading"><h1>{tr("Prompt poster AI miễn phí - Update thường xuyên")}</h1><p>{tr("Tôi sẽ cập nhật prompt thường xuyên, hoặc nếu bạn có nhu cầu cần thêm prompt về bất kỳ lĩnh vực nào, hãy liên hệ tôi, sự góp sức của bạn sẽ giúp website trở nên lớn mạnh.")}</p></header>
    <div className="prompt-library-layout">
      <aside className="prompt-category-sidebar" aria-label={tr("Phân loại prompt")}>
        <div className="prompt-category-sidebar-head"><p>{tr("THƯ VIỆN PROMPT")}</p><h2>{tr("Chọn chủ đề")}</h2></div>
        <nav>{promptCategories.map(category => {
          const count = templates.filter(template => promptTemplateTaxonomy[template.id]?.category === category.id).length;
          return <button key={category.id} type="button" className={category.id === categoryId ? 'is-active' : ''} onClick={() => chooseCategory(category.id)} aria-current={category.id === categoryId ? 'page' : undefined}>
            <span><strong>{tr(category.label)}</strong><small>{tr(category.description)}</small></span><em>{count}</em>
          </button>;
        })}</nav>
      </aside>
      <main className="prompt-library-content">
        <div className="prompt-subcategory-bar">
          <div><p>{tr("PHÂN LOẠI")}</p><h2>{tr(activeCategory.label)}</h2></div>
          <nav aria-label={tr(`Nhóm con của ${activeCategory.label}`)}>{activeCategory.subcategories.map(subcategory => {
            const count = subcategory.id === 'all'
              ? categoryTemplates.length
              : categoryTemplates.filter(template => promptTemplateTaxonomy[template.id]?.subcategory === subcategory.id).length;
            return <button key={subcategory.id} type="button" className={subcategory.id === subcategoryId ? 'is-active' : ''} onClick={() => setSubcategoryId(subcategory.id)} aria-pressed={subcategory.id === subcategoryId}>{tr(subcategory.label)}<span>{count}</span></button>;
          })}</nav>
        </div>
        <div className="catalog-toolbar prompt-library-toolbar">
          <label className="inline-search"><Icon name="search" /><input value={query} onChange={event => setQuery(event.target.value)} placeholder={tr("Tìm tên mẫu, phong cách hoặc nguyên liệu...")} aria-label={tr("Tìm trong thư viện prompt")} /></label>
          <p><strong>{filtered.length}</strong>{tr(" mẫu phù hợp")}</p>
        </div>
        {filtered.length ? <div className="prompt-card-grid" ref={resultsRef} tabIndex={-1} role="region" aria-label={tr('Danh sách prompt')}>{visibleTemplates.map(template => {
      const saveId = `prompt-${template.id}`;
      const saved = savedIds.includes(saveId);
      return <article className="prompt-template-card" key={template.id}>
        <a className="prompt-card-open" data-prompt-id={template.id} href={promptTemplateHref(template.id)} aria-label={tr(`Mở mẫu ${template.cardTitle}`)}>
          <img src={imageUrl(template)} alt={tr(template.thumbnailAlt)} width="640" height="480" loading="lazy" />
        </a>
        <div className="prompt-card-meta"><a className="prompt-card-title" href={promptTemplateHref(template.id)}>{tr(template.cardTitle)}</a><button type="button" className={`prompt-save-button ${saved ? 'is-saved' : ''}`} onClick={() => toggleSaved(saveId)} aria-label={saved ? tr(`Bỏ lưu ${template.cardTitle}`) : tr(`Lưu ${template.cardTitle}`)} aria-pressed={saved}><Icon name="bookmark" size={17}/>{saved ? tr('Đã lưu') : tr('Lưu mẫu')}</button></div>
      </article>;
        })}</div> : <div className="empty-state prompt-library-empty"><span className="icon-tile large"><Icon name={needle ? 'search' : 'spark'} /></span><h2>{needle ? tr('Không tìm thấy prompt') : tr(`Chưa có mẫu ${tr(activeCategory.label).toLocaleLowerCase(locale)}`)}</h2><p>{needle ? tr('Thử tên sản phẩm, nguyên liệu hoặc phong cách ngắn hơn.') : tr('Các mẫu thuộc nhóm này sẽ được bổ sung dần. Bạn có thể chọn một chủ đề khác để tiếp tục.')}</p></div>}
        {pageCount > 1 && <nav className="prompt-pagination" aria-label={tr('Phân trang prompt')}>
          <p role="status" aria-atomic="true">{tr(`Hiển thị ${pageStart + 1}–${pageStart + visibleTemplates.length} trong ${filtered.length} mẫu`)}</p>
          <div className="prompt-pagination-controls">
            <button type="button" className="prompt-page-previous" aria-label={tr('Trang trước')} disabled={currentPage === 1} onClick={() => choosePage(currentPage - 1)}><Icon name="arrow" size={16}/>{tr('Trước')}</button>
            {Array.from({ length: pageCount }, (_, index) => index + 1).map(page => <button type="button" key={page} className={page === currentPage ? 'is-active' : ''} aria-label={tr(`Trang ${page}`)} aria-current={page === currentPage ? 'page' : undefined} onClick={() => choosePage(page)}>{page}</button>)}
            <button type="button" aria-label={tr('Trang sau')} disabled={currentPage === pageCount} onClick={() => choosePage(currentPage + 1)}>{tr('Sau')}<Icon name="arrow" size={16}/></button>
          </div>
        </nav>}
      </main>
    </div>
    {active && <PromptEditor key={active.id} template={active} onClose={closeEditor} />}
  </div>;
}

export function SavedPromptCard({ templateId, saved, onToggleSaved }: { templateId: string; saved: boolean; onToggleSaved: (id: string) => void }) {
  const tr = useTranslation();
  const template = getPromptTemplate(templateId);
  if (!template) return null;
  const saveId = `prompt-${template.id}`;
  const destination = promptTemplateHref(template.id);
  return <article className="resource-card saved-card saved-prompt-card" aria-label={tr(template.cardTitle)}>
    <div className="resource-card-cover saved-prompt-card-cover">
      <img className="saved-prompt-card-image" src={imageUrl(template)} alt={tr(template.thumbnailAlt)} width="640" height="480" loading="lazy" />
      <button type="button" className="resource-card-save" aria-pressed={saved} onClick={() => onToggleSaved(saveId)} aria-label={tr(saved ? `Bỏ lưu ${template.cardTitle}` : `Lưu ${template.cardTitle}`)}><Icon name="bookmark" size={17} /></button>
    </div>
    <div className="resource-card-body">
      <span className="resource-card-category">{tr('Prompt')}</span>
      <h2 className="resource-card-title"><a className="saved-card-link" href={destination}>{tr(template.cardTitle)}</a></h2>
      <p className="resource-card-summary">{tr(template.title)}</p>
      <div className="resource-card-meta">{template.fields.length}{tr(' trường có thể chỉnh')}</div>
    </div>
  </article>;
}

export function PromptEditor({ template, onClose }: { template: PromptTemplate; onClose: () => void }) {
  const tr = useTranslation();
  const locale = useLocale();
  const [values, setValues] = useState<PromptValues>(() => promptDraftRepository.read(template));
  const [output, setOutput] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const outputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({ preventScroll: true });
    const handleKeys = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (event.key !== 'Tab') return;
      const dialog = closeRef.current?.closest('.prompt-editor-dialog');
      const focusable = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary')) : [];
      if (!focusable.length) return;
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', handleKeys);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeys);
      if (previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus({ preventScroll: true });
      else document.querySelector<HTMLElement>(`[data-prompt-id="${template.id}"]`)?.focus({ preventScroll: true });
    };
  }, [onClose, template.id]);
  useEffect(() => promptDraftRepository.write(template, values), [template, values]);

  const update = (key: string, value: string) => { setValues(current => ({ ...current, [key]: value })); setErrors([]); setCopied(false); };
  const generate = () => {
    const missing = getMissingPromptFields(template, values);
    setErrors(missing);
    if (missing.length) return;
    const operation = startOperation('prompt-builder');
    setOutput(renderPrompt(template, values));
    operation.complete();
    requestAnimationFrame(() => outputRef.current?.focus());
  };
  const reset = () => { promptDraftRepository.clear(template); setValues(createPromptValues(template)); setOutput(''); setErrors([]); setCopied(false); };
  const copy = async () => {
    if (!output) return;
    try { await navigator.clipboard.writeText(output); setCopied(true); }
    catch { outputRef.current?.select(); document.execCommand('copy'); setCopied(true); }
  };
  const primary = template.fields.filter(field => !field.advanced);
  const advanced = template.fields.filter(field => field.advanced);

  return createPortal(<div className="prompt-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="prompt-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="prompt-editor-title">
      <header className="prompt-editor-header"><div><p>{tr(template.caption)}</p><h2 id="prompt-editor-title">{template.cardTitle}</h2></div><button ref={closeRef} className="icon-button" type="button" onClick={onClose} aria-label={tr("Đóng trình tạo prompt")}><Icon name="close" /></button></header>
      <div className="prompt-editor-body">
        <section className="prompt-preview-panel" aria-label={tr("Ảnh mẫu poster")}>
          <div className="prompt-preview-media"><img className="prompt-preview-image" src={imageUrl(template)} alt={tr(template.thumbnailAlt)} width="640" height="480" /></div>
          <p className="prompt-preview-description">{tr(template.title)}</p>
        </section>
        <form className="prompt-editor-form" onSubmit={event => { event.preventDefault(); generate(); }}>
          <div className="prompt-form-content">
          {errors.length > 0 && <div className="prompt-error" role="alert"><strong>{tr("Hãy điền các trường bắt buộc:")}</strong> {errors.map(label => tr(label)).join(', ')}</div>}
          <fieldset><legend>{tr("Thông tin chính")}</legend><p className="field-help">{tr(template.referenceImage ? "Khi tạo ảnh, đính kèm ảnh sản phẩm cùng prompt. Không cần nhập lại thương hiệu hay tên sản phẩm." : "Prompt mẫu dùng tiếng Anh; nội dung bạn nhập không được dịch tự động.")}</p><div className="prompt-fields">{primary.map(field => <PromptFieldControl key={field.key} field={field} value={values[field.key] ?? ''} onChange={value => update(field.key, value)} />)}</div></fieldset>
          {advanced.length > 0 && <details className="prompt-advanced"><summary>{tr(template.advancedLabel || 'Tùy chỉnh nâng cao')}<span>{advanced.length}{tr(" mục")}</span></summary><div className="prompt-fields">{advanced.map(field => <PromptFieldControl key={field.key} field={field} value={values[field.key] ?? ''} onChange={value => update(field.key, value)} />)}</div></details>}
          </div>
          <div className="prompt-form-actions"><button className="prompt-reset" type="button" onClick={reset}>{tr("Đặt lại")}</button><button className="prompt-generate" type="submit"><Icon name="spark" size={18}/>{tr(" Tạo prompt")}</button></div>
        </form>
        <section className="prompt-output-panel" aria-label={tr("Prompt hoàn chỉnh")}>
          <div className="prompt-output-head"><div><span>{tr("KẾT QUẢ")}</span><h3>{tr("Prompt hoàn chỉnh")}</h3></div><span>{output.length.toLocaleString(locale)}{tr(" ký tự")}</span></div>
          {output ? <><textarea ref={outputRef} value={output} onChange={event => { setOutput(event.target.value); setCopied(false); }} aria-label={tr("Nội dung prompt hoàn chỉnh")}/><div className="prompt-output-actions"><button type="button" className="prompt-copy" onClick={copy}><Icon name={copied ? 'spark' : 'scan'} size={18}/>{copied ? tr('Đã sao chép') : tr('Sao chép prompt')}</button><a href="https://gemini.google.com/app" target="_blank" rel="noreferrer" onClick={() => void copy()}>{tr("Mở Gemini ")}<Icon name="external" size={15}/></a><a href="https://www.dola.com/chat/create-image" target="_blank" rel="noreferrer" onClick={() => void copy()}>{tr("Mở Dola ")}<Icon name="external" size={15}/></a></div></> : <div className="prompt-output-empty"><span className="icon-tile large"><Icon name="spark" /></span><h3>{tr("Prompt sẽ xuất hiện ở đây")}</h3><p>{tr("Điền thông tin và bấm “Tạo prompt”. Bạn vẫn có thể sửa nội dung trước khi sao chép.")}</p></div>}
        </section>
      </div>
    </section>
  </div>, document.body);
}

function PromptFieldControl({ field, value, onChange }: { field: PromptTemplate['fields'][number]; value: string; onChange: (value: string) => void }) {
  const tr = useTranslation();
  const id = `prompt-field-${field.key}`;
  return <label className="prompt-field" htmlFor={id}><span>{tr(field.label)}{field.optional && <small>{tr("Tùy chọn")}</small>}</span>{field.options ? <select id={id} value={value} onChange={event => onChange(event.target.value)}>{field.options.map(option => <option key={option} value={option}>{tr(option)}</option>)}</select> : <textarea id={id} rows={value.length > 70 ? 3 : 1} value={value} placeholder={tr(field.placeholder)} required={!field.optional} onChange={event => onChange(event.target.value)} />}</label>;
}
